/**
 * The brand's tools a custom tool can combine: all of them but the custom ones, which do not nest, and
 * the ones the Kim platform runs itself, which the tool server never runs. Looked up by integration and
 * name, the way a step names its tool.
 */

import { useCallback, useMemo } from 'react';
import { useToolDefinitions } from '@/hooks/use-tool-definitions';
import { toolKey } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { CUSTOM_PROVIDER } from './paths';

/**
 * Anything not marked a read counts as a write, as the tool server counts it when it runs the custom
 * tool: a tool still loading, or one this brand does not have, included.
 */
export const isWriteTool = (tool: ToolDefinitionAction | undefined): boolean => tool?.riskLevel !== 'low';

export const useStepTools = () => {
	const { data, isLoading, isError } = useToolDefinitions();
	const tools = useMemo(() => (data ?? []).filter((tool) => tool.provider !== CUSTOM_PROVIDER && !tool.platformHandled), [data]);
	const byKey = useMemo(() => new Map(tools.map((tool) => [toolKey(tool.provider, tool.tool), tool])), [tools]);
	const toolFor = useCallback(
		(step: { provider: string; tool: string }): ToolDefinitionAction | undefined => byKey.get(toolKey(step.provider, step.tool)),
		[byKey]
	);
	return { tools, toolFor, isLoading, isError };
};

export type StepTools = ReturnType<typeof useStepTools>;
